import Column from '@components/Column';
import Image from '@components/Image';
import Row from '@components/Row';
import Text from '@components/Text';
import colors from '@constants/colors';
import styled from '@emotion/styled';
import usePriceUnit from '@hooks/item/usePriceUnit';
import { DiscountPrepaidCardRow } from '@models/discount';
import useCalculatedRequest from '@store/order/hooks/useCalculatedRequest';

interface Props {
    card: DiscountPrepaidCardRow;
    isSelected: boolean;
    onToggle: () => void;
}

/** 선불카드 한 장 (이미지·이름·잔액). 잔액이 없으면 선택할 수 없다 */
function PrepaidCardRow({ card, isSelected, onToggle }: Props) {
    const priceUnit = usePriceUnit();
    const { totalAmount, totalDiscount, giftAmount } = useCalculatedRequest();
    const isEmptyBalance = card.balance <= 0;
    // 잔액이 없거나, 할인·선물로 남은 금액이 없으면 고를 수 없다
    const disabled = isEmptyBalance
        || (!isSelected && totalAmount - totalDiscount - giftAmount <= 0);
    const textColor = disabled ? colors.textUnselected : colors.textPrimary;

    return (
        <Container fullWidth
            spacing={2}
            aria-label='prepaid-card-item'
            data-amount={card.balance}
            data-name={card.name}
            data-selected={isSelected}
            aria-disabled={disabled}
            onClick={() => {
                if (!disabled) {
                    onToggle();
                }
            }}>
            <Info spacing={3}>
                <CardImage src={card.cardImageUrl} width={60} height={40} alt={card.name}/>
                <Texts spacing={1}>
                    <Text fontWeight='medium' color={textColor} ellipsis>{card.name}</Text>
                    <Text size='body-long' fontWeight='medium' color={textColor}>
                        {card.balance.toLocaleString()}{priceUnit}
                    </Text>
                </Texts>
            </Info>
            {/* [26.10.26 승인] 잔액이 0원일 때 충전하기 버튼은 임시로 숨기고 비활성화한다.
                KICC 잔액 갱신이 5초 이상 걸려, 충전 후 결제 화면으로 돌아오면
                금액이 갱신되기 전에 결제될 수 있다.
                {isEmptyBalance && (
                    <ChargeButton variant='tone'
                        aria-label='charge-prepaid-card-button'
                        size='xsmall'
                        disabled
                        onClick={(e) => {
                            e.stopPropagation();
                            push('PrepaidCardChargeActivity', { prepaidCardId: card.id });
                        }}>
                        {t('prepaidSheet.charge')}
                    </ChargeButton>
                )}
            */}
        </Container>
    );
}

const Container = styled(Row)`
    background-color: var(--color-background-pure);
    border: 1px solid var(--color-border-default);
    border-radius: var(--radius-sm);
    cursor: pointer;
    padding: var(--spacing-4);

    &[data-selected="true"] {
        background-color: var(--color-background-base);
        border-color: var(--color-icon-accent);
    }

    &[aria-disabled="true"] {
        cursor: default;
    }
`;

const Info = styled(Row)`
    flex: 1;
    min-width: 0;
`;

const Texts = styled(Column)`
    flex: 1;
    min-width: 0;
`;

const CardImage = styled(Image)`
    border-radius: var(--radius-xs);
    flex-shrink: 0;
    height: 40px;
    object-fit: cover;
    width: 60px;
`;

export default PrepaidCardRow;
